import styled from "styled-components";

// Buyer-facing "quick order" panel (product-order-content.tsx). Mirrors
// make-order/subs/ReqForm.tsx's ReqFormWrapper — the same product-attribute
// grid (23%-wide info items in a flex-wrap row) and .line divider — since
// both hit ORDERS_URL to create an order and should read as one visual
// system rather than two different-looking forms for the same action.
export const ProductOrderContentWrapper = styled.div`
  width: 45rem;
  max-width: 100%;
  padding: 1.5rem;
  background-color: ${({ theme }) => theme.bg.prim_500};

  .header {
    display: flex;
    gap: 1rem;
    margin-bottom: 1.5rem;

    img {
      height: 8rem;
      width: 8rem;
      flex-shrink: 0;
      border-radius: 0.25rem;
      object-fit: cover;
    }

    .info {
      display: flex;
      min-width: 0;
      flex-direction: column;
      justify-content: center;
      gap: 0.5rem;

      h2 {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        font-size: 1rem;
        font-weight: 600;
        color: ${({ theme }) => theme.text.primary};
      }

      .company {
        margin-top: 0.125rem;
        font-size: 0.625rem;
        font-weight: 500;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        color: ${({ theme }) => theme.text.prim_100};
      }
    }
  }

  .stats {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 1.5rem 1.1rem;
    padding: 0.85rem;

    > * {
      width: 23%;

      @media (max-width: ${({ theme }) => theme.screen.mobile}) {
        width: 46%;
      }
    }
  }

  .line {
    width: 90%;
    height: 1px;
    margin: 0 auto;
    background-color: ${({ theme }) => theme.border.light};
  }

  .section {
    margin: 1.5rem 0;

    .section-title {
      margin-bottom: 0.5rem;
      font-size: 0.75rem;
      font-weight: 600;
      letter-spacing: 0.03em;
      text-transform: uppercase;
      color: ${({ theme }) => theme.text.prim_100};
    }
  }

  .batch-list {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 1rem;

    .batch-card {
      display: flex;
      align-items: center;
      gap: 1rem;
      border: 1px solid ${({ theme }) => theme.border.light};
      border-radius: 0.25rem;
      padding: 0.75rem;

      &.invalid {
        border-color: ${({ theme }) => theme.text.danger};
      }

      .batch-info {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 0.25rem;

        .batch-title {
          font-size: 0.875rem;
          font-weight: 500;
          color: ${({ theme }) => theme.text.primary};
        }

        .batch-meta {
          font-size: 0.75rem;
          color: ${({ theme }) => theme.text.prim_100};

          .strike {
            text-decoration: line-through;
            color: ${({ theme }) => theme.text.prim_200};
          }

          .discounted {
            font-weight: 600;
            color: ${({ theme }) => theme.text.success};
          }
        }

        .batch-error {
          font-size: 0.75rem;
          color: ${({ theme }) => theme.text.danger};
        }
      }

      input {
        width: 6rem;
        flex-shrink: 0;
        padding: 0.5rem;
        border: 1px solid ${({ theme }) => theme.border.input};
        border-radius: 0.2rem;
        outline: none;
        font-size: 0.875rem;

        &:focus {
          border-color: ${({ theme }) => theme.bg.secondary};
        }
      }
    }
  }
`;

// Same visual formula as the shared Table component's order-status pill
// (.status-inner/.status-dot in Table/subs/style.ts): tinted background,
// colored dot, colored label, no border — kept as one reusable status
// language across the app instead of a one-off badge style.
export const StatusPill = styled.span<{ color: string }>`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  border-radius: 0.5rem;
  background-color: ${({ color }) => color}20;

  .status-dot {
    width: 0.4rem;
    height: 0.4rem;
    border-radius: 50%;
    background-color: ${({ color }) => color};
  }

  span {
    font-size: 0.8rem;
    font-weight: 500;
    text-transform: capitalize;
    color: ${({ color }) => color};
  }
`;
